import type { CanvasRenderContext } from '../core';
import { InteractiveObject } from '../core/editor-object';

export type ArrangeTileSurface = HTMLImageElement | HTMLCanvasElement;

export interface ArrangeImageTileOptions {
  x: number;
  y: number;
  borderWidth?: number;
  borderColor?: string | null;
}

/**
 * A freely draggable image tile used by the editor's arrange mode (e.g. the
 * custom merge layout). Only movement is allowed — no scaling, rotation or
 * resize handles.
 */
export class ArrangeImageTile extends InteractiveObject {
  override type = 'arrange-image-tile';

  private surface: ArrangeTileSurface;
  private tileBorderWidth = 0;
  private tileBorderColor: string | null = null;

  constructor(id: string, surface: ArrangeTileSurface, options: ArrangeImageTileOptions) {
    super({
      id,
      type: 'arrange-image-tile',
      originX: 'left',
      originY: 'top',
      left: options.x,
      top: options.y,
      width: resolveSurfaceSize(surface).width,
      height: resolveSurfaceSize(surface).height,
      strokeWidth: 0,
      fill: null,
      selectable: true,
      evented: true,
      hasControls: false,
      hasBorders: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      lockSkewingX: true,
      lockSkewingY: true,
      hoverCursor: 'move',
      moveCursor: 'grabbing',
      borderColor: '#3678fc',
      borderScaleFactor: 2,
      borderDashArray: null,
      borderOpacityWhenMoving: 0.9,
    });
    this.surface = surface;
    this.setTileStyle(options.borderWidth ?? 0, options.borderColor ?? null);
  }

  setSurface(surface: ArrangeTileSurface): this {
    if (this.surface === surface) {
      return this;
    }

    this.surface = surface;
    const size = resolveSurfaceSize(surface);
    this.width = size.width;
    this.height = size.height;
    this.dirty = true;
    this.setCoords();
    return this;
  }

  setTileStyle(borderWidth: number, borderColor: string | null): this {
    this.tileBorderWidth = Math.max(0, Math.round(borderWidth || 0));
    this.tileBorderColor = borderColor;
    this.dirty = true;
    return this;
  }

  override render(ctx: CanvasRenderingContext2D, _context: CanvasRenderContext): void {
    const [a, b, c, d, e, f] = this.calcTransformMatrix();

    ctx.save();
    ctx.globalAlpha = this.opacity;
    ctx.transform(a, b, c, d, e, f);
    ctx.drawImage(this.surface, -this.width / 2, -this.height / 2, this.width, this.height);

    if (this.tileBorderWidth > 0 && this.tileBorderColor) {
      ctx.strokeStyle = this.tileBorderColor;
      ctx.lineWidth = this.tileBorderWidth;
      ctx.strokeRect(-this.width / 2, -this.height / 2, this.width, this.height);
    }

    ctx.restore();
  }
}

function resolveSurfaceSize(surface: ArrangeTileSurface): { width: number; height: number } {
  const image = surface as HTMLImageElement;
  return {
    width: Math.max(1, image.naturalWidth || surface.width || 1),
    height: Math.max(1, image.naturalHeight || surface.height || 1),
  };
}
